// public/v3/src/pages/settings.jsx — provider-personal preferences and
// (admin-gated) BHI practice settings.
//
// Personal display/workflow settings are stored in localStorage via
// lib/user-prefs. Most legacy practice-level toggles (CCM/APCM combined
// consent, AI model, etc.) still live under "Settings (advanced) →
// /v2#settings" in the More drawer. BHI practice settings (bhi_enabled,
// bhi_enabled_scales, bhi_custom_scales, bulk_bhi_bn_template) are managed
// here via the v3 settings API (admin role required to write).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ccmApi } from '../lib/api';
import { safeAudit } from '../lib/consent-audit';
import { getPref, setPref } from '../lib/user-prefs';
import { DEFAULT_TAB_ORDER, getOrderedTabs, getVisibleTabs, normalizeHiddenTabs, normalizeToolbarOrder, tabsForRole } from '../lib/tabs';
import { BHI_SCALES_BUILTIN } from '../lib/bhi-scales';
import { DEFAULT_ALLOWLIST_CLASSES, PAYER_CLASS_VALUES, resolvePayerAllowlistFromSettings } from '../lib/payer-rules';

const CUSTOM_SCALE_ID_RX = /^[A-Z][A-Z0-9_-]*$/;

const PAYER_CLASS_LABELS = {
  medicare: 'Medicare (FFS)',
  ma: 'Medicare Advantage',
  dual: 'Dual (Medicare + Medicaid)',
  medicaid: 'Medicaid only',
  commercial: 'Commercial',
  unknown: 'Unknown (no insurance on file)',
};

const REVIEW_TAB_OPTIONS = [
  { v: 'careplan', label: 'Care plan' },
  { v: 'billing',  label: 'Billing note' },
  { v: 'timelog',  label: 'Time log' },
];

function _parseSigDataUrl(dataUrl) {
  const m = String(dataUrl || '').match(/^data:image\/(png|jpe?g);base64,(.+)$/i);
  if (!m) return null;
  return { format: m[1].toLowerCase() === 'png' ? 'png' : 'jpg', base64: m[2] };
}

// Self-service e-signature: each user sets their OWN signature (the server keys
// it to the authenticated user). Embedded on Quest home-draw orders. Draw on a
// canvas (iPad/Pencil) or upload a PNG/JPG; preview shows how it prints.
// Per-practice non-connected labs for the Order Chat fax-requisition flow.
// Stored under settings key `genericLabsConfig` as [{ name, elationVendorId,
// accountNumber, faxNumber, aliases }] (see server/services/genericLabReq/).
// The card auto-syncs the practice's PRINT-ONLY Elation vendors — one fixed
// row per vendor; a lab participates once it has an account # or fax saved.
// Manual rows remain for the rare case the vendor fetch is unavailable.
const _labKey = (s) => String(s || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');

// Group-home refill sheets in Order Chat (O154, O170; PR-E), shown right under the
// refill-sheet model row. Keys must stay byte-identical with server/services/voice.js
// (isOrderChatRosterEnabled / isOrderChatRosterAutoDraftEnabled) — a renamed key
// silently no-ops the toggle. The two defaults differ on purpose: the feature is OFF
// until a practice turns it on; auto-draft is ON with no row (O170: "drafting refills is
// a low risk action"), so it reads `!== false` and only an explicit false turns it off.
export function RefillSheetToggles({ settings, disabled, onSave }) {
  const s = settings || {};
  return (
    <>
      <div className="settings-row" data-setting="orderChatRosterEnabled">
        <div style={{ flex: 1 }}>
          <div className="settings-row-label">Group-home refill sheets in Order Chat</div>
          <div className="settings-row-desc">
            Adds “Refill sheet…” to Order Chat: a provider uploads a group home's refill sheet (a
            photo or a PDF) and each line is matched to that resident's chart. The group home needs
            its Elation tags set (Kiosk admin → Facilities). Off by default.
          </div>
        </div>
        <label className="settings-toggle">
          <input
            type="checkbox"
            checked={s.orderChatRosterEnabled === true}
            disabled={disabled}
            onChange={(e) => onSave('orderChatRosterEnabled', e.target.checked)}
            aria-label="Turn on group-home refill sheets in Order Chat for this practice"
          />
          <span className="settings-toggle-slider"/>
        </label>
      </div>
      <div className="settings-row" data-setting="orderChatRosterAutoDraft">
        <div style={{ flex: 1 }}>
          <div className="settings-row-label">Draft confident refill-sheet lines automatically</div>
          <div className="settings-row-desc">
            On (the default): a line that matches exactly one resident and one medication on their
            chart is saved straight to Elation as an unsigned refill draft for the provider to sign,
            like the fax inbox's refill drafting. Off: every line waits for the provider on a card.
          </div>
        </div>
        <label className="settings-toggle">
          <input
            type="checkbox"
            checked={s.orderChatRosterAutoDraft !== false}
            disabled={disabled}
            onChange={(e) => onSave('orderChatRosterAutoDraft', e.target.checked)}
            aria-label="Draft confident refill-sheet lines automatically"
          />
          <span className="settings-toggle-slider"/>
        </label>
      </div>
    </>
  );
}

// Live voice — speech model. The picker shows what the SERVER will run: the
// stored value normalised the way server/services/voice.js getLiveSpeechModel
// normalises it (trim, lower-case, catalogue ids only), else the tenant
// default — never the first option by accident (PR #809 review).
const LIVE_SPEECH_MODEL_IDS = ['gpt-live-1', 'gpt-realtime-2.1', 'gpt-realtime-2.1-mini'];
const LIVE_SPEECH_MODEL_DEFAULT = 'gpt-realtime-2.1-mini';
function liveSpeechModelValue(stored) {
  const id = String(stored || '').trim().toLowerCase();
  return LIVE_SPEECH_MODEL_IDS.includes(id) ? id : LIVE_SPEECH_MODEL_DEFAULT;
}

function GenericLabsCard({ value, disabled, onSave }) {
  const [vendors, setVendors] = useState(null); // null = loading, [] = none/failed
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    let alive = true;
    ccmApi.getPrintVendors()
      .then((r) => { if (alive) setVendors((r && r.vendors) || []); })
      .catch(() => { if (alive) setVendors([]); });
    return () => { alive = false; };
  }, []);

  // Merge Elation's print-only vendors with the saved config: one synced row
  // per vendor (name fixed), plus editable manual rows for saved entries that
  // match no current vendor. Rebuilds only while there are no unsaved edits.
  useEffect(() => {
    if (dirty || vendors === null) return;
    const saved = Array.isArray(value) ? value : [];
    const claimed = new Set();
    const synced = vendors.map((v) => {
      const hit = saved.find((r) => r.elationVendorId && String(r.elationVendorId) === String(v.vendorId))
        || saved.find((r) => !r.elationVendorId && _labKey(r.name) === _labKey(v.vendorName));
      if (hit) claimed.add(hit);
      return {
        synced: true,
        elationVendorId: String(v.vendorId),
        name: v.vendorName,
        accountNumber: (hit && hit.accountNumber) || '',
        faxNumber: (hit && hit.faxNumber) || '',
        aliases: hit && Array.isArray(hit.aliases) ? hit.aliases : [],
      };
    });
    const manual = saved.filter((r) => !claimed.has(r)).map((r) => ({
      synced: false,
      elationVendorId: r.elationVendorId || null,
      name: r.name || '',
      accountNumber: r.accountNumber || '',
      faxNumber: r.faxNumber || '',
      aliases: Array.isArray(r.aliases) ? r.aliases : [],
    }));
    setRows([...synced, ...manual]);
  }, [value, vendors, dirty]);

  const upd = (i, k, v) => { setDirty(true); setMsg(''); setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r))); };
  const add = () => { setDirty(true); setMsg(''); setRows((rs) => [...rs, { synced: false, elationVendorId: null, name: '', accountNumber: '', faxNumber: '', aliases: [] }]); };
  // Synced rows can't be removed (they mirror Elation) — clearing their values
  // turns the fax flow off for that lab; manual rows are simply dropped.
  const clearOrRemove = (i) => {
    setDirty(true); setMsg('');
    setRows((rs) => rs[i] && rs[i].synced
      ? rs.map((r, j) => (j === i ? { ...r, accountNumber: '', faxNumber: '' } : r))
      : rs.filter((_, j) => j !== i));
  };

  async function save() {
    // A row with no account # AND no fax is "not configured" — omitted so the
    // order-time tagger never matches it.
    const cleaned = rows
      .map((r) => ({
        name: r.name.trim(),
        elationVendorId: r.elationVendorId || null,
        accountNumber: r.accountNumber.trim(),
        faxNumber: r.faxNumber.replace(/\D/g, ''),
        aliases: r.aliases,
      }))
      .filter((r) => r.name && (r.accountNumber || r.faxNumber));
    const badFax = cleaned.find((r) => r.faxNumber && (r.faxNumber.length < 10 || r.faxNumber.length > 11));
    if (badFax) { setMsg(`Fax for "${badFax.name}" must be 10-11 digits.`); return; }
    setBusy(true); setMsg('');
    try {
      const ok = await onSave(cleaned);
      if (ok !== false) { setDirty(false); setMsg('Saved.'); }
      else setMsg('Could not save.');
    } catch (e) {
      setMsg((e && e.message) || 'Could not save.');
    } finally { setBusy(false); }
  }

  const inputStyle = { fontSize: 13, padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, width: '100%' };
  const activeRow = (r) => !!(r.accountNumber.trim() || r.faxNumber.trim());
  return (
    <div className="settings-row settings-row-stack">
      <div className="settings-row-head">
        <div style={{ flex: 1 }}>
          <div className="settings-row-label">Non-connected labs: fax requisitions</div>
          <div className="settings-row-desc">
            Print-only lab vendors are synced from Elation automatically — fill in the account
            number and requisition fax to turn on the Order Chat “Review &amp; fax” flow for a
            lab (leave both blank to keep it off). The account number prints on the requisition;
            the fax is the lab's intake line (editable per-send). Vendors newly added in Elation
            can take up to an hour to appear here.
          </div>
        </div>
      </div>
      {vendors === null && <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading Elation lab vendors…</div>}
      {vendors !== null && rows.length === 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
          No print-only lab vendors are configured in Elation (Settings → Labs).
        </div>
      )}
      {rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto auto', gap: 6, alignItems: 'center', marginTop: 8 }}>
          <div style={{ fontSize: 11, color: '#6b7280' }}>Lab (from Elation)</div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>Account #</div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>Fax</div>
          <div />
          <div />
          {rows.map((r, i) => (
            <React.Fragment key={r.elationVendorId || `manual-${i}`}>
              {r.synced ? (
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{r.name}</div>
              ) : (
                <input style={inputStyle} value={r.name} disabled={disabled || busy} onChange={(e) => upd(i, 'name', e.target.value)} placeholder="Lab name (as in Elation)" aria-label="Lab name" />
              )}
              <input style={inputStyle} value={r.accountNumber} disabled={disabled || busy} onChange={(e) => upd(i, 'accountNumber', e.target.value)} placeholder="12345678" aria-label={`Account number for ${r.name || 'lab'}`} />
              <input style={inputStyle} value={r.faxNumber} disabled={disabled || busy} onChange={(e) => upd(i, 'faxNumber', e.target.value)} placeholder="7185551234" aria-label={`Fax number for ${r.name || 'lab'}`} />
              <span style={{ fontSize: 11, fontWeight: 600, color: activeRow(r) ? '#047857' : '#9ca3af' }}>
                {activeRow(r) ? 'Fax flow on' : 'Off'}
              </span>
              <button type="button" disabled={disabled || busy} onClick={() => clearOrRemove(i)}
                style={{ fontSize: 12, color: '#b91c1c', background: 'none', border: 'none', cursor: 'pointer' }}>
                {r.synced ? 'Clear' : 'Remove'}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
        {vendors !== null && vendors.length === 0 && (
          <button type="button" disabled={disabled || busy} onClick={add}
            style={{ fontSize: 13, fontWeight: 600, color: '#0f766e', background: '#ecfdf5', border: '1px solid #99f6e4', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>
            + Add lab manually
          </button>
        )}
        {dirty && (
          <button type="button" disabled={disabled || busy} onClick={save}
            style={{ fontSize: 13, fontWeight: 600, color: '#fff', background: '#0f766e', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>
            {busy ? 'Saving…' : 'Save labs'}
          </button>
        )}
        {msg && <span style={{ fontSize: 12, color: msg === 'Saved.' ? '#047857' : '#b45309' }}>{msg}</span>}
      </div>
    </div>
  );
}

function MySignatureCard() {
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);  // saved signature, as a data URL
  const [pending, setPending] = useState(null);  // newly captured, not yet saved
  const [mode, setMode]       = useState('upload');
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState('');
  const [info, setInfo]       = useState('');
  const [faxAutoSign, setFaxAutoSign] = useState(false); // self-scoped fax auto-sign consent
  const canvasRef  = useRef(null);
  const drawingRef = useRef(false);
  const dirtyRef   = useRef(false);

  useEffect(() => {
    let alive = true;
    ccmApi.getMySignature()
      .then((r) => {
        if (!alive || !r) return;
        if (r.hasSignature && r.base64) setCurrent(`data:image/${r.format === 'jpg' ? 'jpeg' : 'png'};base64,${r.base64}`);
        setFaxAutoSign(!!r.faxAutoSignOptIn);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const _toggleFaxAutoSign = async (next) => {
    setFaxAutoSign(next); setError(''); setInfo(''); // optimistic
    try { await ccmApi.setFaxAutoSign(next); setInfo(next ? 'Fax auto-sign enabled.' : 'Fax auto-sign disabled.'); }
    catch (e) { setFaxAutoSign(!next); setError((e && e.message) || 'Could not update fax auto-sign.'); }
  };

  // Size the canvas backing store for crisp lines on HiDPI / iPad.
  useEffect(() => {
    if (mode !== 'draw') return;
    const c = canvasRef.current; if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const w = c.clientWidth || 480, h = c.clientHeight || 140;
    c.width = w * ratio; c.height = h * ratio;
    const ctx = c.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#111';
    ctx.clearRect(0, 0, w, h);
    dirtyRef.current = false;
  }, [mode]);

  const _pos = (e) => { const r = canvasRef.current.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const _down = (e) => { e.preventDefault(); const c = canvasRef.current; c.setPointerCapture && c.setPointerCapture(e.pointerId); const ctx = c.getContext('2d'); const p = _pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); drawingRef.current = true; };
  const _move = (e) => { if (!drawingRef.current) return; const ctx = canvasRef.current.getContext('2d'); const p = _pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); dirtyRef.current = true; };
  const _up   = () => { drawingRef.current = false; };
  const _clearPad = () => { const c = canvasRef.current; if (!c) return; c.getContext('2d').clearRect(0, 0, c.width, c.height); dirtyRef.current = false; };
  const _useDrawing = () => { if (!dirtyRef.current) { setError('Draw your signature first.'); return; } setError(''); setPending(canvasRef.current.toDataURL('image/png')); };

  const _onFile = (e) => {
    const f = e.target.files && e.target.files[0]; e.target.value = '';
    if (!f) return;
    if (!/^image\/(png|jpe?g)$/i.test(f.type)) { setError('Use a PNG or JPG image.'); return; }
    if (f.size > 300 * 1024) { setError('Image is too large (max 300 KB).'); return; }
    const rd = new FileReader();
    rd.onload = () => { setError(''); setInfo(''); setPending(String(rd.result)); };
    rd.onerror = () => setError("Couldn't read that file.");
    rd.readAsDataURL(f);
  };

  const _save = async () => {
    const parsed = _parseSigDataUrl(pending);
    if (!parsed) { setError('Capture or upload a signature first.'); return; }
    setBusy(true); setError(''); setInfo('');
    try { await ccmApi.setMySignature(parsed.format, parsed.base64); setCurrent(pending); setPending(null); setInfo('Signature saved.'); }
    catch (e) { setError((e && e.message) || 'Save failed.'); }
    finally { setBusy(false); }
  };
  const _remove = async () => {
    setBusy(true); setError(''); setInfo('');
    try { await ccmApi.clearMySignature(); setCurrent(null); setPending(null); setInfo('Signature removed.'); }
    catch (e) { setError((e && e.message) || 'Remove failed.'); }
    finally { setBusy(false); }
  };

  const preview = pending || current;
  return (
    <div className="settings-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
      <div>
        <div className="settings-row-label">My signature</div>
        <div className="settings-row-desc">
          Your handwritten signature prints in the Ordering Physician box on Quest home-draw orders
          you place. Draw it (best on iPad) or upload a PNG/JPG. Only you can set your own.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn ghost sm" onClick={() => { setMode('upload'); setPending(null); }} disabled={busy} aria-pressed={mode === 'upload'}>Upload</button>
        <button type="button" className="btn ghost sm" onClick={() => { setMode('draw'); setPending(null); }} disabled={busy} aria-pressed={mode === 'draw'}>Draw</button>
      </div>

      {mode === 'upload' ? (
        <label className="btn ghost sm" style={{ alignSelf: 'flex-start', cursor: 'pointer' }}>
          Choose image…
          <input type="file" accept="image/png,image/jpeg" onChange={_onFile} disabled={busy} style={{ display: 'none' }} />
        </label>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
          <canvas ref={canvasRef} width={480} height={140}
            style={{ width: 480, maxWidth: '100%', height: 140, border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', touchAction: 'none' }}
            onPointerDown={_down} onPointerMove={_move} onPointerUp={_up} onPointerLeave={_up} onPointerCancel={_up} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn ghost sm" onClick={_clearPad} disabled={busy}>Clear pad</button>
            <button type="button" className="btn ghost sm" onClick={_useDrawing} disabled={busy}>Use this drawing</button>
          </div>
        </div>
      )}

      {preview && (
        <div>
          <div className="settings-row-desc" style={{ marginBottom: 4 }}>Preview (as it prints):</div>
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', padding: 8, display: 'inline-block' }}>
            <img src={preview} alt="signature preview" style={{ height: 44, maxWidth: 240, objectFit: 'contain', display: 'block' }} />
          </div>
        </div>
      )}

      {error && <div className="settings-row-desc" style={{ color: '#b91c1c' }}>{error}</div>}
      {info && <div className="settings-row-desc" style={{ color: '#047857' }}>{info}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn ghost sm" onClick={_save} disabled={busy || loading || !pending}>Save signature</button>
        {current && <button type="button" className="btn ghost sm" onClick={_remove} disabled={busy}>Remove</button>}
      </div>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: loading ? 'default' : 'pointer' }}>
        <input
          type="checkbox"
          checked={faxAutoSign}
          disabled={loading || busy}
          onChange={(e) => _toggleFaxAutoSign(e.target.checked)}
          style={{ marginTop: 2 }}
        />
        <span className="settings-row-desc" style={{ margin: 0 }}>
          Let the bot apply my signature to incoming forms (fax auto-sign)
        </span>
      </label>
    </div>
  );
}

export function SettingsPage({
  open,
  onClose,
  density,
  setDensity,
  hideNoChronic,
  setHideNoChronic,
  // hiddenOnboardTags/setHiddenOnboardTags retired with the Onboard tag chips
  // (insurance lenses replaced them); props kept so call sites don't break.
  hiddenOnboardTags,        // eslint-disable-line no-unused-vars
  setHiddenOnboardTags,     // eslint-disable-line no-unused-vars
  toolbarOrder,
  hiddenTabs,
  onToolbarOrderChange,
  onHiddenTabsChange,
  user,
  onBhiEnabledChange,
  onPainEnabledChange,
  onPinEnabledChange,
}) {
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
  const [defaultTab, setDefaultTabState]               = useState('timelogs');
  const [defaultReviewTab, setDefaultReviewTabState]   = useState('careplan');
  const [confirmBeforePush, setConfirmBeforePushState] = useState(false);
  const [keyboardShortcuts, setKeyboardShortcutsState] = useState(true);
  const [toolbarOrderState, setToolbarOrderState]       = useState(DEFAULT_TAB_ORDER);
  const [hiddenTabsState, setHiddenTabsState]           = useState([]);

  // Practice-level settings (server-side, loaded via ccmApi.getSettings).
  // Admin-only writes; non-admins see the BHI sections read-only with a notice.
  const [settings, setSettings]                         = useState({});
  const [settingsLoading, setSettingsLoading]           = useState(false);
  const [settingsError, setSettingsError]               = useState('');
  const [settingsSaving, setSettingsSaving]             = useState(false);
  const [bhiInlineError, setBhiInlineError]             = useState('');
  const [customScaleEditor, setCustomScaleEditor]       = useState(null); // null | { mode: 'create'|'edit', original: scale|null, draft }

  // Payer import filter (sync_payer_allowlist / sync_payer_admission).
  // null = filter disabled (all payer classes admitted at sync).
  const payerFilter = useMemo(() => resolvePayerAllowlistFromSettings(settings), [settings]);
  const [medicaidAdmitTags, setMedicaidAdmitTags] = useState('');
  useEffect(() => {
    if (settingsLoading) return;
    const tags = (payerFilter && payerFilter.admission.medicaid && payerFilter.admission.medicaid.tags) || [];
    setMedicaidAdmitTags(tags.join(', '));
    // Re-sync from server state once per settings load — not per keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsLoading]);

  // Program auto-tags (elation_auto_tags): per-program tag names the app
  // writes TO Elation when a patient becomes enrolled. Blank = family off.
  const AUTO_TAG_FAMILIES = [
    ['ccm', 'CCM'], ['apcm', 'APCM'], ['bhi', 'BHI'], ['pain', 'Pain (CPM)'],
  ];
  const [autoTags, setAutoTags] = useState({ ccm: '', apcm: '', bhi: '', pain: '' });
  useEffect(() => {
    if (settingsLoading) return;
    const cfg = settings.elation_auto_tags && typeof settings.elation_auto_tags === 'object' ? settings.elation_auto_tags : {};
    setAutoTags({
      ccm: cfg.ccm || '', apcm: cfg.apcm || '', bhi: cfg.bhi || '', pain: cfg.pain || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsLoading]);
  function saveAutoTags() {
    const out = {};
    for (const [key] of AUTO_TAG_FAMILIES) {
      const v = String(autoTags[key] || '').trim();
      if (v) out[key] = v;
    }
    saveSetting('elation_auto_tags', out);
  }

  // BHI consent-date backfill (admin maintenance). Patients enrolled before
  // the first-visit default landed have bhiConsent.date stamped with the
  // enrollment day; the scan finds those (date === enrolledAt date, i.e.
  // never operator-edited) and proposes the first Elation visit instead.
  const [backfillBusy, setBackfillBusy]                 = useState(false);
  const [backfillRows, setBackfillRows]                 = useState(null); // null | [{id, name, oldDate, firstVisit, action}]
  const [backfillNoLink, setBackfillNoLink]             = useState(0);
  const [backfillResult, setBackfillResult]             = useState('');
  const [backfillError, setBackfillError]               = useState('');

  // Local mirror for the Note Expander auto-sweep keyword field. We save on
  // blur (not per-keystroke) since the settings POST is admin-only and
  // per-keystroke writes would spam it. Defaults to 'expand' when unset.
  const [autoSweepKeyword, setAutoSweepKeyword] = useState('');
  useEffect(() => {
    setAutoSweepKeyword(
      typeof settings.noteExpanderAutoSweepKeyword === 'string'
        ? settings.noteExpanderAutoSweepKeyword : 'expand');
  }, [settings.noteExpanderAutoSweepKeyword]);

  const activeToolbarOrder = normalizeToolbarOrder(toolbarOrderState);
  const activeHiddenTabs = normalizeHiddenTabs(hiddenTabsState, activeToolbarOrder);
  const orderedToolbarTabs = getOrderedTabs(activeToolbarOrder);
  const visibleToolbarTabs = getVisibleTabs(activeToolbarOrder, activeHiddenTabs);
  const hiddenSet = new Set(activeHiddenTabs);
  // Role-filter the default-tab choices so a non-admin can't pick an admin-only
  // tab (e.g. Kiosk) as their default and land on an "admins only" dead-end.
  const defaultTabOptions = tabsForRole(visibleToolbarTabs, {
    role: user?.role,
    features: user?.features,
    featurePermissionsEnabled: user?.featurePermissionsEnabled,
  }).map(t => ({ v: t.v, label: t.full }));

  useEffect(() => {
    if (!open) return;
    setDefaultReviewTabState(getPref('defaultReviewTab'));
    setConfirmBeforePushState(getPref('confirmBeforePush'));
    setKeyboardShortcutsState(getPref('keyboardShortcuts'));
    const nextOrder = normalizeToolbarOrder(toolbarOrder || getPref('toolbarOrder'));
    const nextHidden = normalizeHiddenTabs(hiddenTabs || getPref('hiddenTabs'), nextOrder);
    const nextVisible = getVisibleTabs(nextOrder, nextHidden);
    const savedDefault = getPref('defaultTab');
    const nextDefault = nextVisible.some(t => t.v === savedDefault) ? savedDefault : nextVisible[0]?.v || 'timelogs';
    setDefaultTabState(nextDefault);
    if (nextDefault !== savedDefault) setPref('defaultTab', nextDefault);
    setToolbarOrderState(nextOrder);
    setHiddenTabsState(nextHidden);
  }, [open, toolbarOrder, hiddenTabs]);

  // Load practice-level settings (BHI flags, billing-note template, etc.)
  // every time the panel opens. Non-admins can read but cannot save.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSettingsError('');
    setSettingsLoading(true);
    setBhiInlineError('');
    setCustomScaleEditor(null);
    ccmApi.getSettings()
      .then((s) => {
        if (!cancelled) setSettings(s && typeof s === 'object' ? s : {});
      })
      .catch((e) => {
        if (!cancelled) setSettingsError(e?.message || 'Could not load practice settings');
      })
      .finally(() => {
        if (!cancelled) setSettingsLoading(false);
      });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);

  function pickDensity(v) {
    setDensity?.(v);
    setPref('density', v);
  }
  function pickDefaultTab(v) {
    setDefaultTabState(v);
    setPref('defaultTab', v);
  }
  function pickDefaultReviewTab(v) {
    setDefaultReviewTabState(v);
    setPref('defaultReviewTab', v);
  }
  function toggleConfirmPush() {
    const next = !confirmBeforePush;
    setConfirmBeforePushState(next);
    setPref('confirmBeforePush', next);
  }
  function toggleShortcuts() {
    const next = !keyboardShortcuts;
    setKeyboardShortcutsState(next);
    setPref('keyboardShortcuts', next);
  }
  function saveToolbarPrefs(nextOrder, nextHidden = activeHiddenTabs) {
    const normalizedOrder = normalizeToolbarOrder(nextOrder);
    const normalizedHidden = normalizeHiddenTabs(nextHidden, normalizedOrder);
    const visible = getVisibleTabs(normalizedOrder, normalizedHidden);
    setToolbarOrderState(normalizedOrder);
    setHiddenTabsState(normalizedHidden);
    setPref('toolbarOrder', normalizedOrder);
    setPref('hiddenTabs', normalizedHidden);
    onToolbarOrderChange?.(normalizedOrder);
    onHiddenTabsChange?.(normalizedHidden);

    if (!visible.some(t => t.v === defaultTab)) {
      pickDefaultTab(visible[0]?.v || 'timelogs');
    }
  }
  function moveToolbarTab(tabId, delta) {
    const next = [...activeToolbarOrder];
    const from = next.indexOf(tabId);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= next.length) return;
    [next[from], next[to]] = [next[to], next[from]];
    saveToolbarPrefs(next);
  }
  function toggleToolbarTab(tabId) {
    const nextHidden = new Set(activeHiddenTabs);
    if (nextHidden.has(tabId)) {
      nextHidden.delete(tabId);
    } else {
      const visibleCount = activeToolbarOrder.filter(id => !nextHidden.has(id)).length;
      if (visibleCount <= 1) return;
      nextHidden.add(tabId);
    }
    saveToolbarPrefs(activeToolbarOrder, Array.from(nextHidden));
  }
  function resetToolbar() {
    saveToolbarPrefs(DEFAULT_TAB_ORDER, []);
  }
  // ── Practice-level (server-side) settings ────────────────────────────────
  // saveSetting writes one key to /data/settings (POST is admin-only). On
  // success we mirror the value into local `settings` state so the UI stays
  // in sync without a round-trip. On failure we surface the message and
  // revert via a re-fetch.
  function saveFeaturePermissions(enabled) {
    if (enabled) {
      const ok = window.confirm(
        'Turn on feature permissions? Staff will be limited to Onboard, Time Logs, and Settings ' +
        'unless you assign them a broader custom role. Admins and providers are unaffected. ' +
        'You can turn this off at any time.'
      );
      if (!ok) return;
    }
    saveSetting('feature_permissions_enabled', enabled);
  }

  function togglePayerClass(cls) {
    if (!payerFilter) return;
    const has = payerFilter.classes.includes(cls);
    const next = has ? payerFilter.classes.filter((c) => c !== cls) : [...payerFilter.classes, cls];
    // Never write an empty list — the server treats it as "fall back to the
    // default", which would silently re-check three boxes on the next load.
    if (!next.length) return;
    saveSetting('sync_payer_allowlist', { classes: next });
  }

  function saveMedicaidAdmitTags() {
    const tags = medicaidAdmitTags.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);
    const current = ((payerFilter && payerFilter.admission.medicaid && payerFilter.admission.medicaid.tags) || []).join(',');
    if (tags.join(',') === current) return; // unchanged — skip the write
    const existing = (settings.sync_payer_admission && typeof settings.sync_payer_admission === 'object')
      ? settings.sync_payer_admission : {};
    const next = { ...existing };
    if (tags.length) next.medicaid = { tags };
    else delete next.medicaid;
    saveSetting('sync_payer_admission', next);
  }

  async function saveSetting(key, value) {
    if (!isAdmin) {
      setBhiInlineError('Only an admin can change practice settings.');
      return false;
    }
    const prev = settings;
    setSettings({ ...prev, [key]: value });
    setSettingsSaving(true);
    setBhiInlineError('');
    try {
      await ccmApi.saveSettings({ [key]: value });
      // Notify parent when bhi_enabled changes so chrome can show/hide the
      // CCM/BHI filter toggle without a page reload.
      if (key === 'bhi_enabled') onBhiEnabledChange?.(value === true);
      if (key === 'pain_enabled') onPainEnabledChange?.(value === true);
      if (key === 'pin_enabled') onPinEnabledChange?.(value === true);
      return true;
    } catch (e) {
      // Revert optimistic update; re-fetch authoritative state.
      setSettings(prev);
      try { const s = await ccmApi.getSettings(); setSettings(s && typeof s === 'object' ? s : {}); }
      catch (_) { /* leave reverted state */ }
      setBhiInlineError(e?.message || 'Could not save setting.');
      return false;
    } finally {
      setSettingsSaving(false);
    }
  }

  // ── BHI consent-date backfill ─────────────────────────────────────────────
  // A consent is a backfill candidate only when its date equals the date-part
  // of bhi.enrolledAt (both stamped in UTC at enrollment) — i.e. it still
  // carries the old "today" default and was never operator-edited. Dates that
  // differ from the enrollment day were set deliberately and are left alone.
  function bhiBackfillCandidate(data) {
    const consent = data?.bhiConsent;
    const enrolledAt = data?.bhi?.enrolledAt;
    if (!consent || consent.signed !== true || !consent.date || !enrolledAt) return false;
    return consent.date === String(enrolledAt).slice(0, 10);
  }

  async function scanBhiConsentBackfill() {
    setBackfillBusy(true);
    setBackfillError(''); setBackfillResult(''); setBackfillRows(null);
    try {
      const rows = await ccmApi.listPatients();
      const candidates = [];
      let noLink = 0;
      for (const row of rows || []) {
        const d = row?.data || {};
        if (!bhiBackfillCandidate(d)) continue;
        if (!d.elation_id) { noLink++; continue; }
        candidates.push({
          id: row.id,
          elationId: String(d.elation_id),
          name: [d.fname, d.lname].filter(Boolean).join(' ') || row.id,
          oldDate: d.bhiConsent.date,
        });
      }
      // firstVisitDates caps at 200 ids per call.
      const dates = {};
      for (let i = 0; i < candidates.length; i += 200) {
        const res = await ccmApi.firstVisitDates(candidates.slice(i, i + 200).map(c => c.elationId));
        Object.assign(dates, (res && res.results) || {});
      }
      setBackfillRows(candidates.map(c => {
        const fv = dates[c.elationId] || null;
        return {
          ...c,
          firstVisit: fv,
          action: !fv ? 'no visits found' : fv === c.oldDate ? 'already matches' : 'update',
        };
      }));
      setBackfillNoLink(noLink);
    } catch (e) {
      setBackfillError(e?.message || 'Scan failed.');
    } finally {
      setBackfillBusy(false);
    }
  }

  async function applyBhiConsentBackfill() {
    const updates = (backfillRows || []).filter(r => r.action === 'update');
    if (!updates.length) return;
    if (!window.confirm(
      `Rewrite ${updates.length} BHI consent date(s) to the patient's first Elation visit?\n\n` +
      'Only never-edited enrollment-day defaults are touched. Audit-logged per patient.'
    )) return;
    setBackfillBusy(true); setBackfillError('');
    try {
      // Re-fetch and re-check each candidate so a consent edited between the
      // scan and this click is never clobbered by the stale scan snapshot.
      const rows = await ccmApi.listPatients();
      const byId = new Map((rows || []).map(r => [r.id, r]));
      let ok = 0, skipped = 0;
      for (const u of updates) {
        try {
          const d = byId.get(u.id)?.data;
          if (!d || !bhiBackfillCandidate(d) || d.bhiConsent.date !== u.oldDate) { skipped++; continue; }
          await ccmApi.updatePatient(u.id, { ...d, bhiConsent: { ...d.bhiConsent, date: u.firstVisit } });
          await safeAudit('BHI_CONSENT_CHANGE', u.id,
            `BHI consent date backfilled ${u.oldDate} → ${u.firstVisit} (first Elation visit) for ${u.name}`);
          ok++;
        } catch (_) {
          skipped++;
        }
      }
      setBackfillResult(`Updated ${ok} patient(s).` + (skipped ? ` ${skipped} skipped (changed since scan or write failed).` : ''));
      setBackfillRows(null);
    } catch (e) {
      setBackfillError(e?.message || 'Backfill failed.');
    } finally {
      setBackfillBusy(false);
    }
  }

  // ── Per-feature inference model selection (admin-gated) ──────────────────
  // Each key MUST stay byte-identical with server/services/inference/
  // modelResolver.js (INFERENCE_MODEL_KEYS) and the option values byte-identical
  // with ALLOWED_INFERENCE_MODELS — a renamed key or value silently no-ops.
  //
  // bedrockOnly / openaiAllowed mirror the server's OPENAI_ALIASES_BY_FEATURE
  // (modelResolver.js): kiosk intake (vision, not opened) and the batch lane take no
  // OpenAI alias at all; fax form-fill takes Sol only (O158 — its boxes decide where ink
  // lands, and only Sol placed them accurately); the group-home refill-sheet reader takes
  // Sol only (O164 — Luna read Rx#s confident-wrong at every effort) and defaults to
  // Bedrock Sonnet (O167 — Sonnet read no Rx# or name confident-wrong in P0);
  // fax triage takes both. The resolver
  // refuses anything else regardless, so the panel does not offer it — an admin should
  // never be able to choose what the server will quietly refuse.
  const INFERENCE_FEATURES = [
    { key: 'inferenceModelAutoSweep',      label: 'Note Expander auto-sweep' },
    { key: 'inferenceModelFaxTriage',      label: 'Fax triage' },
    { key: 'inferenceModelFormFill',       label: 'Fax form-fill', openaiAllowed: ['openai-sol'] },
    { key: 'inferenceModelStudioClassify', label: 'Studio — classify notes' },
    { key: 'inferenceModelKioskIntake',    label: 'Kiosk intake', bedrockOnly: true },
    { key: 'inferenceModelOrderChat',      label: 'Order chat (SMS)' },
    { key: 'inferenceModelOrderChatLabRerank', label: 'Order chat — lab name re-rank' },
    { key: 'inferenceModelOrderDx',        label: 'Order chat — dx resolution' },
    { key: 'inferenceModelOrderChatRoster', label: 'Order chat — group-home refill sheet (default Sonnet)', openaiAllowed: ['openai-sol'] },
    { key: 'inferenceModelSmsAssistant',   label: 'SMS assistant' },
    { key: 'inferenceModelVoiceAgent',     label: 'Voice assistant' },
    // GPT-Live (server/live): the delegated model runs INSIDE the vendor's voice session,
    // so only an OpenAI alias can serve it — Luna (default) or Sol. Mirrors the resolver's
    // OPENAI_ONLY_FEATURES; Default = Luna. Not used when the speech model (Voice
    // assistant → Live voice — speech model) is a Realtime model: that model is its own brain.
    { key: 'inferenceModelLiveBackend',    label: 'Live voice — backend model (GPT-Live only; default Luna)', openaiOnly: true },
    { key: 'inferenceModelAiProxy',        label: 'Studio — care plan AI' },
    { key: 'inferenceModelAiRevise',       label: 'Studio — revise' },
    { key: 'inferenceModelCareplanBilling', label: 'Batch care-plan / billing', bedrockOnly: true },
    { key: 'inferenceModelNoteExpanderDistill', label: 'Note Expander — feedback distillation' },
    { key: 'inferenceModelProviderLetterDraft', label: 'Provider letter — drafting' },
    { key: 'inferenceModelVoiceMemoNote',       label: 'Voice memo — chart-note drafting' },
  ];
  // One list drives both the per-feature selects and the switch-all select, so the two
  // can never offer different values. `offers` is the one rule for which choices a row
  // shows: every Bedrock choice; an `openai` choice unless the row is bedrockOnly or names
  // its own openaiAllowed list without it.
  const INFERENCE_CHOICES = [
    { value: '',                 label: 'Default' },
    { value: 'bedrock-haiku',    label: 'Haiku' },
    { value: 'bedrock-sonnet',   label: 'Sonnet' },
    { value: 'bedrock-opus-4-6', label: 'Opus' },
    { value: 'openai-luna',      label: 'Luna (OpenAI)', openai: true },
    { value: 'openai-sol',       label: 'Sol (OpenAI)',  openai: true },
  ];
  function offers(f, c) {
    // An OpenAI-only row offers Default and the OpenAI choices, never a Bedrock one.
    if (!c.openai) return !f.openaiOnly || c.value === '';
    if (f.bedrockOnly) return false;
    return !f.openaiAllowed || f.openaiAllowed.includes(c.value);
  }
  // The rows a switch-all pick lands on: the rows that offer it. Default lands
  // everywhere; a Bedrock pick skips the OpenAI-only row (Live voice — backend
  // model); Luna skips form-fill, the refill sheet, kiosk and batch, which keep what
  // they have; Sol skips kiosk and batch. The skip lists come from `offers`, never
  // from this comment.
  function inferenceTargets(choice) {
    return INFERENCE_FEATURES.filter(f => offers(f, choice));
  }
  // …and the rows it skips, from the same rule — the switch-all description lists these,
  // so the copy beside the select can never drift from what the click does (O159 D-2).
  function inferenceSkips(choice) {
    return INFERENCE_FEATURES.filter(f => !offers(f, choice));
  }
  const masterChoice = INFERENCE_CHOICES.find(c =>
    inferenceTargets(c).every(f => (settings[f.key] || '') === c.value));
  const masterInferenceValue = masterChoice ? masterChoice.value : 'mixed';
  async function setAllInference(model) {
    const choice = INFERENCE_CHOICES.find(c => c.value === model);
    if (!choice) return;
    const patch = {}; inferenceTargets(choice).forEach(f => { patch[f.key] = model; });
    setSettings({ ...settings, ...patch });
    setBhiInlineError('');
    try { await ccmApi.saveSettings(patch); } catch (e) { try { const s = await ccmApi.getSettings(); setSettings(s && typeof s === 'object' ? s : {}); } catch (__) {} setBhiInlineError(e?.message || 'Could not save setting.'); }
  }

  // Effective enabled scale list — defaults to all built-ins when unset.
  function effectiveEnabledScaleIds() {
    return Array.isArray(settings.bhi_enabled_scales)
      ? settings.bhi_enabled_scales
      : BHI_SCALES_BUILTIN.map((s) => s.id);
  }

  function isScaleEnabled(scaleId) {
    return effectiveEnabledScaleIds().includes(scaleId);
  }

  function toggleEnabledScale(scaleId, enabled) {
    const cur = effectiveEnabledScaleIds();
    const next = enabled
      ? Array.from(new Set([...cur, scaleId]))
      : cur.filter((id) => id !== scaleId);
    if (settings.bhi_enabled === true && next.length === 0) {
      setBhiInlineError('At least one rating scale must remain enabled while BHI is on.');
      return;
    }
    setBhiInlineError('');
    saveSetting('bhi_enabled_scales', next);
  }

  function customScales() {
    return Array.isArray(settings.bhi_custom_scales) ? settings.bhi_custom_scales : [];
  }

  function openCustomScaleEditor(existing) {
    setBhiInlineError('');
    setCustomScaleEditor({
      mode: existing ? 'edit' : 'create',
      original: existing || null,
      draft: existing
        ? {
            id: existing.id,
            name: existing.name || '',
            maxScore: String(existing.maxScore ?? ''),
            severityBands: (Array.isArray(existing.severityBands) ? existing.severityBands : [])
              .map(([lo, hi, label]) => ({ lo: String(lo), hi: String(hi), label: String(label || '') })),
          }
        : {
            id: '',
            name: '',
            maxScore: '',
            severityBands: [{ lo: '0', hi: '', label: '' }],
          },
      error: '',
    });
  }

  function closeCustomScaleEditor() {
    setCustomScaleEditor(null);
  }

  function updateCustomScaleDraft(patch) {
    setCustomScaleEditor((cur) => cur ? { ...cur, draft: { ...cur.draft, ...patch }, error: '' } : cur);
  }

  function updateCustomScaleBand(idx, patch) {
    setCustomScaleEditor((cur) => {
      if (!cur) return cur;
      const bands = cur.draft.severityBands.slice();
      bands[idx] = { ...bands[idx], ...patch };
      return { ...cur, draft: { ...cur.draft, severityBands: bands }, error: '' };
    });
  }

  function addCustomScaleBand() {
    setCustomScaleEditor((cur) => {
      if (!cur) return cur;
      const bands = cur.draft.severityBands.slice();
      const lastHi = bands.length ? Number(bands[bands.length - 1].hi) : -1;
      const nextLo = Number.isFinite(lastHi) ? String(lastHi + 1) : '';
      bands.push({ lo: nextLo, hi: '', label: '' });
      return { ...cur, draft: { ...cur.draft, severityBands: bands }, error: '' };
    });
  }

  function removeCustomScaleBand(idx) {
    setCustomScaleEditor((cur) => {
      if (!cur) return cur;
      if (cur.draft.severityBands.length <= 1) return cur;
      const bands = cur.draft.severityBands.slice();
      bands.splice(idx, 1);
      return { ...cur, draft: { ...cur.draft, severityBands: bands }, error: '' };
    });
  }

  function setCustomScaleEditorError(message) {
    setCustomScaleEditor((cur) => cur ? { ...cur, error: message } : cur);
  }

  async function saveCustomScale() {
    if (!customScaleEditor) return;
    const { mode, original, draft } = customScaleEditor;
    const id = String(draft.id || '').trim();
    const name = String(draft.name || '').trim();
    const maxScore = Number(draft.maxScore);

    if (!id) return setCustomScaleEditorError('ID is required.');
    if (!CUSTOM_SCALE_ID_RX.test(id)) {
      return setCustomScaleEditorError('ID must start with an uppercase letter and contain only A-Z, 0-9, _ or -.');
    }
    if (!name) return setCustomScaleEditorError('Name is required.');
    if (!Number.isFinite(maxScore) || maxScore < 1 || maxScore > 100) {
      return setCustomScaleEditorError('Max score must be a whole number between 1 and 100.');
    }
    if (!draft.severityBands.length) {
      return setCustomScaleEditorError('At least one severity band is required.');
    }

    const bands = [];
    for (let i = 0; i < draft.severityBands.length; i++) {
      const b = draft.severityBands[i];
      const lo = Number(b.lo);
      const hi = Number(b.hi);
      const label = String(b.label || '').trim();
      if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
        return setCustomScaleEditorError(`Band ${i + 1}: lo and hi must be numbers.`);
      }
      if (lo < 0 || hi < lo || hi > maxScore) {
        return setCustomScaleEditorError(`Band ${i + 1}: 0 ≤ lo ≤ hi ≤ max score (${maxScore}).`);
      }
      if (!label) {
        return setCustomScaleEditorError(`Band ${i + 1}: label is required.`);
      }
      bands.push([lo, hi, label]);
    }

    // Uniqueness — collide against built-ins and (in create or rename) other customs.
    const protectedFrom = mode === 'edit' && original ? original.id : null;
    const builtInCollision = BHI_SCALES_BUILTIN.some((s) => s.id === id);
    const customCollision = customScales().some((s) => s.id === id && s.id !== protectedFrom);
    if (builtInCollision) return setCustomScaleEditorError('ID collides with a built-in scale.');
    if (customCollision) return setCustomScaleEditorError('ID collides with an existing custom scale.');

    const nextScale = { id, name, maxScore, severityBands: bands };
    const cur = customScales();
    const nextList = mode === 'edit' && original
      ? cur.map((s) => (s.id === original.id ? nextScale : s))
      : [...cur, nextScale];

    const ok = await saveSetting('bhi_custom_scales', nextList);
    if (ok) closeCustomScaleEditor();
  }

  async function deleteCustomScale(scaleId) {
    if (!isAdmin) {
      setBhiInlineError('Only an admin can change practice settings.');
      return;
    }
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      const ok = window.confirm(`Delete custom scale "${scaleId}"? Existing assessments using this ID will continue to display.`);
      if (!ok) return;
    }
    const next = customScales().filter((s) => s.id !== scaleId);
    await saveSetting('bhi_custom_scales', next);
  }

  if (!open) return null;

  return (
    <>
      <div className="settings-backdrop" onClick={onClose} />
      <section className="settings-overlay settings-page" role="dialog" aria-label="Settings">
        <header className="onboard-head">
          <div>
            <h2>Preferences</h2>
            <div className="onboard-sub">Personal to this browser. Practice settings (admin only) appear below.</div>
          </div>
          <button className="btn ghost icon" onClick={onClose} aria-label="Close">✕</button>
        </header>

        <div style={{ padding: '0 24px 24px', overflowY: 'auto' }}>
          <div className="settings-section">
            <h3 className="settings-section-title">Display</h3>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Density</div>
                <div className="settings-row-desc">Tighten row heights and chrome for more content per screen.</div>
              </div>
              <Segmented value={density} onChange={pickDensity} options={[
                { v: 'cozy',    label: 'Cozy' },
                { v: 'compact', label: 'Compact' },
              ]}/>
            </div>
          </div>

          <div className="settings-section">
            <h3 className="settings-section-title">Roster filters</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
              <input
                type="checkbox"
                checked={!!hideNoChronic}
                onChange={(e) => setHideNoChronic(e.target.checked)}
              />
              <span>Hide patients with no chronic conditions on file</span>
            </label>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
              Applies across Time Logs, Review &amp; Push, APCM Fallback, and Onboard.
            </div>

            {/* The per-tag chip-hiding control was retired with the Onboard
                tag chips — Onboard now filters by insurance lenses. The
                hiddenOnboardTags browser pref remains in user-prefs (dormant)
                so older sessions don't error. */}
          </div>

          <div className="settings-section">
            <h3 className="settings-section-title">Payer import filter</h3>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 6 }}>
              Which payer classes a sync may ADD to the panel. Import-only: existing
              patients are refreshed, never removed, whatever is selected here.
            </div>
            {payerFilter === null ? (
              <div className="settings-row settings-row-stack">
                <div className="settings-row-desc">
                  Filter is currently disabled — syncs admit patients of every payer class.
                </div>
                <button
                  className="btn ghost sm"
                  type="button"
                  disabled={!isAdmin || settingsSaving}
                  onClick={() => saveSetting('sync_payer_allowlist', { classes: DEFAULT_ALLOWLIST_CLASSES })}>
                  Enable filter (Medicare, MA, dual)
                </button>
              </div>
            ) : (
              <>
                <div className="settings-tag-list" role="list" aria-label="Admitted payer classes">
                  {PAYER_CLASS_VALUES.map((cls) => {
                    const checked = payerFilter.classes.includes(cls);
                    return (
                      <div className="settings-tag-item" role="listitem" key={cls}>
                        <label className="settings-tag-visible">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!isAdmin || settingsSaving || (checked && payerFilter.classes.length === 1)}
                            onChange={() => togglePayerClass(cls)}
                          />
                          <span>{PAYER_CLASS_LABELS[cls] || cls}</span>
                        </label>
                      </div>
                    );
                  })}
                </div>
                <div className="settings-row settings-row-stack" style={{ marginTop: 8 }}>
                  <div className="settings-row-head">
                    <div>
                      <div className="settings-row-label">Medicaid admission tags</div>
                      <div className="settings-row-desc">
                        Admit Medicaid-only patients (CoCM / Health Home panels) when they
                        carry one of these Elation tags — even while the Medicaid class is
                        unchecked above. Comma-separated; blank disables tag admission.
                      </div>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={medicaidAdmitTags}
                    placeholder="cocm, health home"
                    disabled={!isAdmin || settingsSaving}
                    onChange={(e) => setMedicaidAdmitTags(e.target.value)}
                    onBlur={saveMedicaidAdmitTags}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  />
                </div>
              </>
            )}
            <div className="settings-row settings-row-stack" style={{ marginTop: 8 }}>
              <div className="settings-row-head">
                <div>
                  <div className="settings-row-label">Program auto-tags (written to Elation)</div>
                  <div className="settings-row-desc">
                    When a patient becomes enrolled in a program here, the app adds that
                    program's tag to their Elation chart (additive only — tags are never
                    removed; a patient in several programs gets each tag). Blank disables
                    a program.
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {AUTO_TAG_FAMILIES.map(([key, label]) => (
                  <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                    <span>{label}</span>
                    <input
                      type="text"
                      value={autoTags[key]}
                      placeholder={label === 'Pain (CPM)' ? 'CPM' : label}
                      disabled={!isAdmin || settingsSaving}
                      style={{ width: 120 }}
                      onChange={(e) => setAutoTags((cur) => ({ ...cur, [key]: e.target.value }))}
                      onBlur={saveAutoTags}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    />
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h3 className="settings-section-title">Workflow</h3>

            <div className="settings-row settings-row-stack">
              <div className="settings-row-head">
                <div>
                  <div className="settings-row-label">Toolbar tabs</div>
                  <div className="settings-row-desc">Choose which tabs show in the top toolbar and the order they appear.</div>
                </div>
                <button className="btn ghost sm" type="button" onClick={resetToolbar}>Reset</button>
              </div>
              <div className="settings-toolbar-list" role="list" aria-label="Toolbar tab order">
                {orderedToolbarTabs.map((t, idx) => {
                  const isHidden = hiddenSet.has(t.v);
                  const canHide = isHidden || visibleToolbarTabs.length > 1;
                  return (
                    <div className={`settings-toolbar-item ${isHidden ? 'is-hidden' : ''}`} role="listitem" key={t.v}>
                      <label className="settings-toolbar-visible">
                        <input
                          type="checkbox"
                          checked={!isHidden}
                          disabled={!canHide}
                          onChange={() => toggleToolbarTab(t.v)}
                        />
                        <span className="settings-toolbar-name">{t.full}</span>
                      </label>
                      <span className={`settings-toolbar-state ${isHidden ? 'is-hidden' : ''}`}>
                        {isHidden ? 'Hidden' : 'Shown'}
                      </span>
                      <div className="settings-toolbar-actions">
                        <button
                          className="settings-toolbar-order-btn"
                          type="button"
                          aria-label={`Move ${t.full} earlier`}
                          disabled={idx === 0}
                          onClick={() => moveToolbarTab(t.v, -1)}>
                          ↑
                        </button>
                        <button
                          className="settings-toolbar-order-btn"
                          type="button"
                          aria-label={`Move ${t.full} later`}
                          disabled={idx === orderedToolbarTabs.length - 1}
                          onClick={() => moveToolbarTab(t.v, 1)}>
                          ↓
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Default landing tab</div>
                <div className="settings-row-desc">Tab shown when the app first opens.</div>
              </div>
              <Segmented value={defaultTab} onChange={pickDefaultTab} options={defaultTabOptions}/>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Default Review &amp; Push sub-tab</div>
                <div className="settings-row-desc">Document shown first when you select a patient.</div>
              </div>
              <Segmented value={defaultReviewTab} onChange={pickDefaultReviewTab} options={REVIEW_TAB_OPTIONS}/>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Confirm before Generate &amp; Push</div>
                <div className="settings-row-desc">Show a confirmation dialog before sending a patient's documents to the EMR.</div>
              </div>
              <label className="settings-toggle">
                <input type="checkbox" checked={confirmBeforePush} onChange={toggleConfirmPush} aria-label="Confirm before Generate and Push"/>
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Keyboard shortcuts</div>
                <div className="settings-row-desc">Use ↑↓ to navigate the Review &amp; Push roster and Esc to close the preview.</div>
              </div>
              <label className="settings-toggle">
                <input type="checkbox" checked={keyboardShortcuts} onChange={toggleShortcuts} aria-label="Keyboard shortcuts"/>
                <span className="settings-toggle-slider"/>
              </label>
            </div>
          </div>

          {/* ── Practice settings (server-side; admin-gated writes) ─────── */}
          <div className="settings-subhead-divider">
            <h3>Practice settings (admin only)</h3>
            <p className="settings-hint">
              These apply to everyone at the practice. Only an admin can change them.
            </p>
          </div>

          <div className="settings-section">
            <h3 className="settings-section-title">Enrollment &amp; consent</h3>
            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Patient consent links</div>
                <div className="settings-row-desc">
                  Let staff send patients a secure one-tap page that explains the care-management
                  program and records their consent electronically. Staff-documented verbal consent
                  keeps working exactly as before. Links are sent from the Onboard tab.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.consent_links_enabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('consent_links_enabled', e.target.checked)}
                  aria-label="Enable patient consent links for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>
          </div>

          <div className="settings-section">
            <h3 className="settings-section-title">Patient scheduling</h3>
            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Patient self-scheduling</div>
                <div className="settings-row-desc">
                  Adds an appointment step to the end of the patient intake link: the patient picks a
                  visit type and a time on the practice calendar, and the appointment is booked into
                  Elation once their chart is created. Hours, visit types and rules are set on the
                  Scheduling tab — the step appears to patients only once a calendar physician, weekly
                  hours and at least one visit type are configured there.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.selfSchedulingEnabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('selfSchedulingEnabled', e.target.checked)}
                  aria-label="Enable patient self-scheduling for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>
          </div>

          <div className="settings-section">
            <h3 className="settings-section-title">Behavioral Health Integration</h3>

            {settingsError && (
              <div className="settings-empty-note error" role="alert">{settingsError}</div>
            )}
            {settingsLoading && !settingsError && (
              <div className="settings-empty-note">Loading practice settings…</div>
            )}
            {!isAdmin && !settingsLoading && !settingsError && (
              <div className="settings-empty-note">
                Read-only view — only an admin can change BHI practice settings.
              </div>
            )}
            {bhiInlineError && (
              <div className="settings-empty-note error" role="alert">{bhiInlineError}</div>
            )}

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Enable BHI program for this practice</div>
                <div className="settings-row-desc">
                  When enabled, BHI uses the practice's combined Care-Management consent (if configured) —
                  one signed consent covers CCM, APCM, and BHI. Otherwise BHI requires its own consent capture in Onboard.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.bhi_enabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('bhi_enabled', e.target.checked)}
                  aria-label="Enable BHI program for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Enable Chronic Pain Management (G3002) for this practice</div>
                <div className="settings-row-desc">
                  Adds the Pain lens (Time Logs, Onboard) for enrolling and tracking CPM patients.
                  G3002 bills on the month's CCM/APCM bill when ≥30 pain-management minutes are logged.
                  Reminder: CPM time is practitioner/QHP time, and enrollment attests an in-person
                  ≥30-minute initiating visit.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.pain_enabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('pain_enabled', e.target.checked)}
                  aria-label="Enable Chronic Pain Management for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Enable Principal Illness Navigation (PIN)</div>
                <div className="settings-row-desc">
                  Adds staff-visible navigation time lines and quick-log presets for PIN-enrolled patients.
                  G0023 requires 60 minutes of navigation services in the month.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.pin_enabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('pin_enabled', e.target.checked)}
                  aria-label="Enable Principal Illness Navigation for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Office assistant (chart Q&amp;A + note drafting)</div>
                <div className="settings-row-desc">
                  When enabled, the Order Chat / SMS bot can also answer questions about a patient's
                  chart (address, phone, insurance, problems, meds, allergies, recent labs) and draft
                  an unsigned note into the chart. Off by default.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.assistantEnabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('assistantEnabled', e.target.checked)}
                  aria-label="Enable office assistant for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Note Expander</div>
                <div className="settings-row-desc">
                  Expand an unsigned visit-note draft into a full ready-to-sign note, with reviewed
                  problem-list and in-office administration writes.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.noteExpanderEnabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('noteExpanderEnabled', e.target.checked)}
                  aria-label="Enable Note Expander"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Note feedback &amp; style learning</div>
                <div className="settings-row-desc">
                  Collect clinician feedback on expanded notes (Chrome extension on Elation +
                  signed-note edit capture) and distill it into per-clinician style preferences.
                  Distilled drafts require review on the Note Expander page before they apply.
                  Requires Note Expander to be on.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.noteExpanderFeedbackEnabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving || settings.noteExpanderEnabled !== true}
                  onChange={(e) => saveSetting('noteExpanderFeedbackEnabled', e.target.checked)}
                  aria-label="Enable Note Expander feedback and style learning"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Auto-expand armed drafts</div>
                <div className="settings-row-desc">
                  When on, a draft whose last line is the keyword below is automatically
                  expanded by a background sweep (every ~5 min). Requires Note Expander to be on.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.noteExpanderAutoSweepEnabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving || settings.noteExpanderEnabled !== true}
                  onChange={(e) => saveSetting('noteExpanderAutoSweepEnabled', e.target.checked)}
                  aria-label="Enable Note Expander auto-sweep"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            <div className="settings-row settings-row-stack">
              <div className="settings-row-head">
                <div style={{ flex: 1 }}>
                  <div className="settings-row-label">Auto-expand keyword</div>
                  <div className="settings-row-desc">
                    The trigger word a draft's last line must match for the background sweep to
                    expand it. Defaults to "expand".
                  </div>
                </div>
              </div>
              <input
                type="text"
                className="settings-select"
                value={autoSweepKeyword}
                placeholder="expand"
                disabled={!isAdmin || settingsLoading || settingsSaving}
                onChange={(e) => setAutoSweepKeyword(e.target.value)}
                onBlur={() => saveSetting('noteExpanderAutoSweepKeyword', autoSweepKeyword.trim() || 'expand')}
                aria-label="Auto-expand keyword"
              />
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Case-sensitive keyword</div>
                <div className="settings-row-desc">
                  When ON, the auto-expand keyword must match the draft's last line with exact
                  letter case. Off (case-insensitive) by default.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.noteExpanderAutoSweepCaseSensitive === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('noteExpanderAutoSweepCaseSensitive', e.target.checked)}
                  aria-label="Case-sensitive auto-expand keyword"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Match keyword at end of last line (ends-with)</div>
                <div className="settings-row-desc">
                  When ON, a draft is armed if its last line <b>ends with</b> the keyword (not only
                  when the whole line equals it); only the trailing keyword is stripped before
                  expansion. Off (whole-line match) by default.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.noteExpanderAutoSweepEndsWith === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('noteExpanderAutoSweepEndsWith', e.target.checked)}
                  aria-label="Match auto-expand keyword at end of last line"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            {/* The "Quest homebound orders: auto-fax" toggle is GONE (O59): a home
                draw is never auto-faxed on any practice. It transmits only from
                "Confirm & fax" under its rendered requisition, so a per-practice
                switch had nothing left to decide — leaving it on screen would have
                claimed a control over transmission that no code reads. */}

            <GenericLabsCard
              value={settings.genericLabsConfig}
              disabled={!isAdmin || settingsLoading || settingsSaving}
              onSave={(list) => saveSetting('genericLabsConfig', list)}
            />

            <MySignatureCard />

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Order Chat agent routing</div>
                <div className="settings-row-desc">
                  When enabled, Order Chat uses the Bedrock agent for patient pivots, revisions, and
                  tool calls, with the existing order extractor as the fallback. Off by default.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.orderChatAgentEnabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('orderChatAgentEnabled', e.target.checked)}
                  aria-label="Enable Order Chat agent routing for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            {/*
              orderChatAutoDxEnabled is read server-side by voice.isOrderChatAutoDxEnabled
              — key must stay byte-identical or the toggle silently no-ops.
            */}
            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Order Chat auto-diagnosis + editable cards</div>
                <div className="settings-row-desc">
                  Auto-pick the most logical covered ICD-10 per test from the chart and show an
                  editable draft card (instead of asking in chat). Applies to home-draw and e-Req
                  lab orders.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.orderChatAutoDxEnabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('orderChatAutoDxEnabled', e.target.checked)}
                  aria-label="Enable Order Chat auto-diagnosis for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            {/*
              providerLetterAutoSend is read by providerLetterMode.js at order
              commit — key must stay byte-identical or the toggle silently no-ops.
            */}
            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Auto-send provider letters ("order X to facility")</div>
                <div className="settings-row-desc">
                  When ON, the provider letter created by "order &lt;study&gt; to &lt;facility&gt;" is
                  signed and faxed to the facility immediately at confirm — no draft review.
                  When OFF (default), the letter is left as an unsigned draft for review in Elation.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.providerLetterAutoSend === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('providerLetterAutoSend', e.target.checked)}
                  aria-label="Auto-send provider letters with attached orders"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            {isAdmin && (
              <div className="settings-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div>
                  <div className="settings-row-label">Backfill BHI consent dates</div>
                  <div className="settings-row-desc">
                    Patients enrolled before first-visit dating landed have their BHI consent dated
                    to the enrollment day. Scan finds never-edited enrollment-day dates and proposes
                    the patient's first Elation office visit; nothing is written until you apply.
                  </div>
                </div>
                <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button className="btn sm" disabled={backfillBusy || settingsLoading} onClick={scanBhiConsentBackfill}>
                    {backfillBusy ? 'Working…' : 'Scan (dry run)'}
                  </button>
                  {backfillRows && backfillRows.some(r => r.action === 'update') && (
                    <button className="btn sm primary" disabled={backfillBusy} onClick={applyBhiConsentBackfill}>
                      Apply {backfillRows.filter(r => r.action === 'update').length} update(s)
                    </button>
                  )}
                </div>
                {backfillError && <div className="settings-empty-note error" role="alert">{backfillError}</div>}
                {backfillResult && <div className="settings-empty-note">{backfillResult}</div>}
                {backfillRows && (
                  <div style={{ marginTop: 8 }}>
                    {backfillRows.length === 0 ? (
                      <div className="settings-empty-note">
                        No enrollment-day BHI consent dates found{backfillNoLink ? ` (${backfillNoLink} candidate(s) skipped — no Elation link)` : ''}.
                      </div>
                    ) : (
                      <>
                        {backfillNoLink > 0 && (
                          <div className="settings-empty-note">{backfillNoLink} candidate(s) skipped — no Elation link.</div>
                        )}
                        <table className="settings-table">
                          <thead>
                            <tr><th>Patient</th><th>Current date</th><th>First visit</th><th>Action</th></tr>
                          </thead>
                          <tbody>
                            {backfillRows.map(r => (
                              <tr key={r.id}>
                                <td>{r.name}</td>
                                <td>{r.oldDate}</td>
                                <td>{r.firstVisit || '—'}</td>
                                <td>{r.action}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="settings-section">
            <h3 className="settings-section-title">Inference & Model</h3>
            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Switch every feature</div>
                <div className="settings-row-desc">
                  Select-all: set every feature below to one model, or back to each feature's
                  default. Opus is higher quality at higher cost; interactive features (chat,
                  voice) are the most expensive and never batch. Luna and Sol (GPT-6) land on
                  every row that offers them — including fax triage, and on Sol fax form-fill
                  and the group-home refill sheet — and that row's prompts and pages go to
                  OpenAI. A row a choice skips keeps what it has.
                  {INFERENCE_CHOICES.filter(c => inferenceSkips(c).length).map((c) => (
                    <span key={c.value}>{' '}{c.label.replace(/ \(OpenAI\)$/, '')} skips: {inferenceSkips(c).map(f => f.label).join(', ')}.</span>
                  ))}
                </div>
              </div>
              <select className="settings-select" value={masterInferenceValue}
                disabled={!isAdmin || settingsLoading || settingsSaving}
                onChange={(e) => setAllInference(e.target.value)}
                aria-label="Switch every feature to one model">
                {masterInferenceValue === 'mixed' && <option value="mixed" disabled>Mixed — set per feature</option>}
                {INFERENCE_CHOICES.map((c) => <option key={c.value || 'default'} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            {INFERENCE_FEATURES.map((f) => (
              <React.Fragment key={f.key}>
              <div className="settings-row settings-row-stack">
                <div className="settings-row-head">
                  <div style={{ flex: 1 }}><div className="settings-row-label">{f.label}</div></div>
                </div>
                <select className="settings-select" value={settings[f.key] || ''}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting(f.key, e.target.value)}
                  aria-label={`${f.label} model`}>
                  {INFERENCE_CHOICES.filter(c => offers(f, c)).map((c) => (
                    <option key={c.value || 'default'} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              {f.key === 'inferenceModelOrderChatRoster' && (
                <RefillSheetToggles
                  settings={settings}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onSave={saveSetting}
                />
              )}
              </React.Fragment>
            ))}
          </div>

          <div className="settings-section">
            <h3 className="settings-section-title">Voice assistant</h3>
            <div className="settings-row-desc" style={{ marginBottom: 10 }}>
              The push-to-talk clinician voice assistant (iOS app + voice endpoints). All three
              switches ship off by default and roll out per practice.
            </div>

            {/*
              Keys must stay byte-identical with server/services/voice.js reads
              (voiceAssistantEnabled / voiceAgentEnabled / voiceSchedulingEnabled /
              practiceTimezone / liveAssistantEnabled / liveVoice / liveSpeechModel /
              liveRosterHints) — a renamed key silently no-ops the feature.
            */}
            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Live voice</div>
                <div className="settings-row-desc">
                  The next-generation assistant: the clinician talks to an OpenAI speech model over
                  the browser (and later the app) — GPT-Live, which hands every clinical request to
                  the backend model chosen under Inference &amp; Model, or a Realtime model that
                  calls the tools itself (the speech-model row below picks which) — and stages the
                  same order cards as Order Chat. Off by default. Audio goes to OpenAI under the
                  BAA; the spoken "yes" sends a card exactly as a typed one does. This switch
                  enables the server side only: the Talk control in Order Chat arrives with the web
                  client release, so turning it on today shows nothing new on the page yet.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.liveAssistantEnabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('liveAssistantEnabled', e.target.checked)}
                  aria-label="Enable Live voice for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Live voice persona</div>
                <div className="settings-row-desc">
                  The voice the assistant speaks with, on either engine. Takes effect on the next session.
                </div>
              </div>
              <select
                className="settings-select"
                value={settings.liveVoice || 'marin'}
                disabled={!isAdmin || settingsLoading || settingsSaving}
                onChange={(e) => saveSetting('liveVoice', e.target.value)}
                aria-label="Live voice persona"
              >
                {['marin', 'cedar', 'alloy', 'ash', 'ballad', 'coral', 'echo', 'sage', 'shimmer', 'verse'].map((v) => (
                  <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
                ))}
              </select>
            </div>

            {/*
              The speech-model list is the server's catalogue (server/live/core/engines.js
              SPEECH_MODELS: id + label, in order); tests/unit/settingsLiveSpeechModels.test.js
              pins this literal to it. An id the server does not know is shown — and run — as the
              tenant default, Realtime 2.1 mini (liveSpeechModelValue).
            */}
            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Live voice — speech model</div>
                <div className="settings-row-desc">
                  Which OpenAI engine the call runs on. Realtime 2.1 mini is the default: it
                  listens, calls the tools and speaks by itself (billed per audio token; the cheapest
                  run; the backend row is not used). GPT-Live is full duplex and hands every clinical
                  request to the backend model chosen under Inference &amp; Model (billed per minute).
                  Takes effect on the next session.
                </div>
              </div>
              <select
                className="settings-select"
                value={liveSpeechModelValue(settings.liveSpeechModel)}
                disabled={!isAdmin || settingsLoading || settingsSaving}
                onChange={(e) => saveSetting('liveSpeechModel', e.target.value)}
                aria-label="Live voice speech model"
              >
                {[['gpt-live-1', 'GPT-Live 1'], ['gpt-realtime-2.1', 'Realtime 2.1'], ['gpt-realtime-2.1-mini', 'Realtime 2.1 mini']].map(([v, label]) => (
                  <option key={v} value={v}>{label}</option>
                ))}
              </select>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Live voice: patient-name hints</div>
                <div className="settings-row-desc">
                  Puts a short list of recently worked-on patient names (the pinned patient plus
                  this provider's recent Order Chat patients, at most 200, names only) into the
                  Live voice prompt so it hears them right. Names leave for OpenAI; off by default.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.liveRosterHints === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('liveRosterHints', e.target.checked)}
                  aria-label="Enable Live voice patient-name hints for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>
            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Enable voice assistant</div>
                <div className="settings-row-desc">
                  Turns on the voice endpoints for this practice (required for the iOS app to work at all).
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.voiceAssistantEnabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('voiceAssistantEnabled', e.target.checked)}
                  aria-label="Enable voice assistant for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Conversational agent</div>
                <div className="settings-row-desc">
                  Routes voice turns through the conversational agent (chart Q&amp;A, compound asks,
                  revisions). Off = the original command-style pipeline only.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.voiceAgentEnabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('voiceAgentEnabled', e.target.checked)}
                  aria-label="Enable the conversational voice agent for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Appointment scheduling (book / reschedule / cancel)</div>
                <div className="settings-row-desc">
                  Lets the voice agent book, move, and cancel appointments. Unlike orders (which are
                  unsigned drafts), appointments commit <strong>live</strong> in Elation the moment
                  the user confirms the readback — there is no draft to review afterward. Requires
                  the conversational agent.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.voiceSchedulingEnabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('voiceSchedulingEnabled', e.target.checked)}
                  aria-label="Enable voice appointment scheduling for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Clinical chart writes (allergies / problems / immunizations / vitals)</div>
                <div className="settings-row-desc">
                  Lets the voice agent record allergies, problem-list entries, historical
                  immunizations, and vitals onto the chart after the spoken confirmation. These are
                  live chart records (not drafts), but each can be undone — "undo" deletes the
                  record. Only curated common conditions/vaccines are accepted. Requires the
                  conversational agent.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.voiceClinicalWritesEnabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('voiceClinicalWritesEnabled', e.target.checked)}
                  aria-label="Enable voice clinical chart writes for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Chart-note drafts from voice memos</div>
                <div className="settings-row-desc">
                  Lets a dictated memo become an UNSIGNED note draft on the patient's Elation
                  chart — created autonomously, without a spoken confirmation ("Note: …" memos
                  auto-draft when everything checks out; others need one review tap). The note
                  is authored by the recorder's own Elation identity and must be reviewed and
                  signed in Elation. Distinct from "clinical chart writes" above, which only
                  covers the live agent's confirmed structured writes.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.voiceMemoNoteDraftsEnabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('voiceMemoNoteDraftsEnabled', e.target.checked)}
                  aria-label="Enable chart-note drafts from voice memos for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Order memos (review-gated draft bundles)</div>
                <div className="settings-row-desc">
                  Lets a dictated "Order: …" memo become a staged bundle of unsigned Elation
                  order drafts that a reviewer confirms with one tap in the Voice Log — orders
                  NEVER auto-commit. Confirming additionally requires the Order Chat
                  permission. Send-now transmits only where a verified destination exists.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.voiceMemoOrdersEnabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('voiceMemoOrdersEnabled', e.target.checked)}
                  aria-label="Enable order memos for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Appointment memos (review-gated scheduling)</div>
                <div className="settings-row-desc">
                  Lets a dictated "Schedule: …" memo become a reviewed booking, move, or
                  cancellation. Confirming writes to the schedule IMMEDIATELY (appointments
                  have no draft state) — the review tap is the gate, and a memo-booked
                  appointment gets a one-tap undo. Also requires "Live appointment
                  scheduling" above and the Order Chat permission to confirm.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.voiceMemoAppointmentsEnabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('voiceMemoAppointmentsEnabled', e.target.checked)}
                  aria-label="Enable appointment memos for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            <div className="settings-row settings-row-stack">
              <div className="settings-row-head">
                <div style={{ flex: 1 }}>
                  <div className="settings-row-label">Voice memo routing</div>
                  <div className="settings-row-desc">
                    How dictated memos are sorted into time logs, chart notes, and other
                    requests. Hybrid (default): a spoken keyword ("note…", "order…") wins,
                    everything else is auto-classified. Also editable on the Voice Log page.
                  </div>
                </div>
              </div>
              <select
                value={['auto', 'hybrid', 'ritual'].includes(settings.voiceMemoRoutingMode) ? settings.voiceMemoRoutingMode : 'hybrid'}
                disabled={!isAdmin || settingsLoading || settingsSaving}
                onChange={(e) => saveSetting('voiceMemoRoutingMode', e.target.value)}
                aria-label="Voice memo routing mode"
                className="settings-select"
              >
                <option value="hybrid">Hybrid — keyword wins, else auto-classify</option>
                <option value="auto">Auto-classify every memo</option>
                <option value="ritual">Keyword only (no keyword → time log)</option>
              </select>
            </div>

            <div className="settings-row settings-row-stack">
              <div className="settings-row-head">
                <div style={{ flex: 1 }}>
                  <div className="settings-row-label">Practice timezone</div>
                  <div className="settings-row-desc">
                    Used to resolve spoken appointment times ("next Tuesday at 2pm") to the
                    practice's local clock. Defaults to Eastern.
                  </div>
                </div>
              </div>
              <select
                value={settings.practiceTimezone || 'America/New_York'}
                disabled={!isAdmin || settingsLoading || settingsSaving}
                onChange={(e) => saveSetting('practiceTimezone', e.target.value)}
                aria-label="Practice timezone"
                className="settings-select"
              >
                <option value="America/New_York">Eastern (America/New_York)</option>
                <option value="America/Chicago">Central (America/Chicago)</option>
                <option value="America/Denver">Mountain (America/Denver)</option>
                <option value="America/Phoenix">Arizona (America/Phoenix)</option>
                <option value="America/Los_Angeles">Pacific (America/Los_Angeles)</option>
                <option value="America/Anchorage">Alaska (America/Anchorage)</option>
                <option value="Pacific/Honolulu">Hawaii (Pacific/Honolulu)</option>
              </select>
            </div>
          </div>

          <div className="settings-section">
            <h3 className="settings-section-title">Depression screening (MIPS CMS2)</h3>
            <div className="settings-row-desc" style={{ marginBottom: 10 }}>
              When enabled, push-and-sign also files a tagged Elation Screening report for any
              patient with a logged PHQ-9 / PHQ-2 in the billed month, feeding the CMS2 MIPS
              depression-screening measure. Patients without a logged screen, or excluded by the
              bipolar/age rules, are skipped. Ships off by default.
            </div>

            {/*
              CMS2 settings keys are intentionally camelCase (cms2ScreeningEnabled /
              cms2FollowUpTag) to stay byte-identical with the push-cqm-screening route's
              settings reads. Do NOT rename to snake_case — the read key and write key MUST
              match or the feature silently no-ops.
            */}
            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Enable CMS2 depression screening for this practice</div>
                <div className="settings-row-desc">
                  Files the Screening report during the existing push-and-sign. No new billable
                  minutes are created. The report is signed in the same provider action.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.cms2ScreeningEnabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveSetting('cms2ScreeningEnabled', e.target.checked)}
                  aria-label="Enable CMS2 depression screening for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>

            <div className="settings-row settings-row-stack">
              <div className="settings-row-head">
                <div style={{ flex: 1 }}>
                  <div className="settings-row-label">Positive-screen follow-up tag</div>
                  <div className="settings-row-desc">
                    Auto-applied to <strong>positive</strong> screens to credit the CMS2 numerator.
                    Choose the value that genuinely describes the behavioral-health care occurring
                    for the patient — the provider's push-signature attests to it. Do not select a
                    therapy modality that is not actually delivered. Only the default
                    (referral to mental-health counseling) is individually confirmed to credit the
                    numerator on its own; the others are value-set members but unverified alone.
                  </div>
                </div>
              </div>
              <select
                value={settings.cms2FollowUpTag || 'Referral to mental health counseling service (procedure)'}
                disabled={!isAdmin || settingsLoading || settingsSaving}
                onChange={(e) => saveSetting('cms2FollowUpTag', e.target.value)}
                aria-label="CMS2 positive-screen follow-up tag"
                className="settings-select"
              >
                <option value="Referral to mental health counseling service (procedure)">
                  Referral to mental health counseling service
                </option>
                <option value="Cognitive and behavioral therapy (regime/therapy)">
                  Cognitive and behavioral therapy
                </option>
                <option value="Individual psychotherapy (regime/therapy)">
                  Individual psychotherapy
                </option>
                <option value="Crisis intervention with follow-up (regime/therapy)">
                  Crisis intervention with follow-up
                </option>
              </select>
            </div>
          </div>

          <div className="settings-section">
            <h3 className="settings-section-title">Access &amp; Permissions</h3>
            <div className="settings-row">
              <div style={{ flex: 1 }}>
                <div className="settings-row-label">Enable feature permissions for this practice</div>
                <div className="settings-row-desc">
                  When enabled, staff are limited to their role's assigned features.
                  Admins and providers are unaffected. Disable at any time to restore full access for all roles.
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={settings.feature_permissions_enabled === true}
                  disabled={!isAdmin || settingsLoading || settingsSaving}
                  onChange={(e) => saveFeaturePermissions(e.target.checked)}
                  aria-label="Enable feature permissions for this practice"
                />
                <span className="settings-toggle-slider"/>
              </label>
            </div>
          </div>

          <div className="settings-section">
            <h3 className="settings-section-title">BHI rating scales</h3>
            <div className="settings-row-desc" style={{ marginBottom: 10 }}>
              Choose which validated scales your practice uses for behavioral health assessment.
              These appear in the assessment picker on Time Logs (BHI view).
            </div>

            <div className="settings-scale-list" role="list" aria-label="Built-in BHI rating scales">
              {BHI_SCALES_BUILTIN.map((scale) => {
                const enabled = isScaleEnabled(scale.id);
                return (
                  <div className={`settings-scale-item ${enabled ? '' : 'is-disabled'}`} role="listitem" key={scale.id}>
                    <label className="settings-scale-toggle">
                      <input
                        type="checkbox"
                        checked={enabled}
                        disabled={!isAdmin || settingsLoading || settingsSaving}
                        onChange={(e) => toggleEnabledScale(scale.id, e.target.checked)}
                      />
                      <span className="settings-scale-name">{scale.name}</span>
                    </label>
                    <span className="settings-scale-meta">
                      Built-in · score 0–{scale.maxScore} · {scale.severityBands.length} severity bands
                    </span>
                  </div>
                );
              })}
            </div>

            <h4 className="settings-subhead">Custom scales</h4>
            {customScales().length === 0 ? (
              <div className="settings-empty-note">(none yet)</div>
            ) : (
              <div className="settings-scale-list" role="list" aria-label="Custom BHI rating scales">
                {customScales().map((scale) => (
                  <div className="settings-scale-item" role="listitem" key={scale.id}>
                    <div className="settings-scale-name">
                      <strong>{scale.id}</strong> — {scale.name}
                    </div>
                    <span className="settings-scale-meta">
                      Custom · score 0–{scale.maxScore} · {(scale.severityBands || []).length} severity bands
                    </span>
                    <div className="settings-scale-actions">
                      <button
                        className="btn ghost sm"
                        type="button"
                        disabled={!isAdmin || settingsSaving}
                        onClick={() => openCustomScaleEditor(scale)}>
                        Edit
                      </button>
                      <button
                        className="btn ghost sm"
                        type="button"
                        disabled={!isAdmin || settingsSaving}
                        onClick={() => deleteCustomScale(scale.id)}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: 12 }}>
              <button
                className="btn sm"
                type="button"
                disabled={!isAdmin || settingsSaving}
                onClick={() => openCustomScaleEditor(null)}>
                + Add custom scale
              </button>
            </div>
          </div>

          <div className="settings-section">
            <h3 className="settings-section-title">BHI billing-note template</h3>
            <div className="settings-row-desc" style={{ marginBottom: 8 }}>
              AI prompt template used when generating standalone BHI billing notes (CPT 99484).
              Leave blank to use the built-in default.
            </div>
            <textarea
              className="settings-textarea"
              rows={10}
              value={settings.bulk_bhi_bn_template || ''}
              disabled={!isAdmin || settingsLoading || settingsSaving}
              onChange={(e) => {
                // Optimistic local update while typing; persist on blur.
                setSettings((s) => ({ ...s, bulk_bhi_bn_template: e.target.value }));
              }}
              onBlur={(e) => saveSetting('bulk_bhi_bn_template', e.target.value)}
              placeholder="(using built-in default)"
              aria-label="BHI billing-note template"
            />
          </div>
        </div>
      </section>
      {customScaleEditor && (
        <CustomScaleEditorModal
          editor={customScaleEditor}
          saving={settingsSaving}
          onChange={updateCustomScaleDraft}
          onChangeBand={updateCustomScaleBand}
          onAddBand={addCustomScaleBand}
          onRemoveBand={removeCustomScaleBand}
          onCancel={closeCustomScaleEditor}
          onSave={saveCustomScale}
        />
      )}
    </>
  );
}

function CustomScaleEditorModal({ editor, saving, onChange, onChangeBand, onAddBand, onRemoveBand, onCancel, onSave }) {
  const { mode, draft, error } = editor;
  const title = mode === 'edit' ? 'Edit custom scale' : 'New custom scale';
  return (
    <div className="modal-backdrop" onClick={() => !saving && onCancel()}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560 }}>
        <div className="modal-head">
          <div>
            <h3>{title}</h3>
            <div className="sub">Practice-defined scale for behavioral-health assessments.</div>
          </div>
        </div>
        <div className="modal-body">
          {error && (
            <div className="settings-empty-note error" role="alert" style={{ marginBottom: 12 }}>{error}</div>
          )}
          <div className="settings-scale-form-row">
            <label className="settings-scale-field">
              <span>ID</span>
              <input
                type="text"
                value={draft.id}
                disabled={mode === 'edit'}
                onChange={(e) => onChange({ id: e.target.value.toUpperCase() })}
                placeholder="MY-SCALE"
                spellCheck={false}
                autoCapitalize="characters"
              />
              <span className="settings-scale-hint">Uppercase A-Z, 0-9, _ or -. Cannot match a built-in.</span>
            </label>
            <label className="settings-scale-field">
              <span>Name</span>
              <input
                type="text"
                value={draft.name}
                onChange={(e) => onChange({ name: e.target.value })}
                placeholder="e.g. Practice insomnia screen"
              />
            </label>
            <label className="settings-scale-field">
              <span>Max score</span>
              <input
                type="number"
                min={1}
                max={100}
                step={1}
                value={draft.maxScore}
                onChange={(e) => onChange({ maxScore: e.target.value })}
              />
            </label>
          </div>

          <div className="settings-scale-form-row">
            <div className="settings-scale-field" style={{ width: '100%' }}>
              <span>Severity bands</span>
              <div className="settings-scale-bands">
                {draft.severityBands.map((band, idx) => (
                  <div className="settings-scale-band" key={idx}>
                    <input
                      type="number"
                      step={1}
                      min={0}
                      value={band.lo}
                      onChange={(e) => onChangeBand(idx, { lo: e.target.value })}
                      aria-label={`Band ${idx + 1} low`}
                      style={{ width: 72 }}
                    />
                    <span aria-hidden="true">–</span>
                    <input
                      type="number"
                      step={1}
                      min={0}
                      value={band.hi}
                      onChange={(e) => onChangeBand(idx, { hi: e.target.value })}
                      aria-label={`Band ${idx + 1} high`}
                      style={{ width: 72 }}
                    />
                    <input
                      type="text"
                      value={band.label}
                      onChange={(e) => onChangeBand(idx, { label: e.target.value })}
                      placeholder="severity label"
                      aria-label={`Band ${idx + 1} label`}
                      style={{ flex: 1, minWidth: 120 }}
                    />
                    <button
                      className="btn ghost sm"
                      type="button"
                      disabled={draft.severityBands.length <= 1}
                      onClick={() => onRemoveBand(idx)}
                      aria-label={`Remove band ${idx + 1}`}>
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <button className="btn ghost sm" type="button" onClick={onAddBand} style={{ marginTop: 8 }}>
                + Add band
              </button>
              <span className="settings-scale-hint">
                Bands use inclusive endpoints (e.g. 0–4 “minimal”, 5–9 “mild”).
              </span>
            </div>
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn ghost" type="button" disabled={saving} onClick={onCancel}>Cancel</button>
          <button className="btn primary" type="button" disabled={saving} onClick={onSave}>
            {saving ? 'Saving…' : (mode === 'edit' ? 'Save changes' : 'Add scale')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="settings-segmented" role="radiogroup">
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          role="radio"
          aria-checked={value === o.v}
          className={`settings-segmented-btn ${value === o.v ? 'active' : ''}`}
          onClick={() => onChange(o.v)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
